import { DateTime } from 'luxon';

import {
    Segment,
    SegmentDefinition,
    SegmentEngagement,
    SegmentFilterState,
    SegmentFilterDemographicFields,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
} from 'domains/segments/types';
import { getDemographicFilter, getEngagementFilter, getEventFilter } from 'domains/segments/utils';
import { CurationSegments, FilterCategory } from 'models/enums';
import { formatPhoneNumber } from 'utils';

import { Detail } from './components/CampaignDetails/CampaignDetails';
import { getFormattedParentAudienceDelayInterval } from './pages/CurationsPage/CampaignDetailsPage/timeFormatting';
import {
    DelayFields,
    Send,
    SendRecurrenceFrequency,
    CampaignChannels,
    ABTestVariant,
    EmailSendInputState,
} from './types';

/**
 * Resolves the campaign-level exit-criteria segment definition for the publish/update payload.
 * Returns the snapshotted definition, or null when no exit criteria is configured so the field
 * is explicitly cleared when none.
 */
export const getExitSegmentDefinition = (campaignState: {
    exitCriteria?: SegmentDefinition | null;
}): SegmentDefinition | null => campaignState.exitCriteria ?? null;

/**
 * Formats SMS sender profile countryCode and number to +1 (999) 999-9999.
 */
export const getFormattedSmsSenderNumber = (
    smsSenderProfile:
        | {
              countryCode?: string;
              number?: string;
          }
        | null
        | undefined
): string => {
    if (!smsSenderProfile?.countryCode && !smsSenderProfile?.number) return '';
    const raw = (smsSenderProfile?.countryCode ?? '') + (smsSenderProfile?.number ?? '');
    const digits = raw.replace(/\D/g, '');
    const toFormat = digits.length === 10 ? '1' + digits : digits;
    return formatPhoneNumber(toFormat);
};

export const getSendDatetimeError = (datetime: DateTime, isDate: boolean) => {
    if (!datetime?.isValid) {
        return `Please enter a valid ${isDate ? 'date' : 'time'}.`;
    }

    if (!(datetime <= DateTime.now().plus({ month: 6 }))) {
        return `Please enter a ${isDate ? 'date' : 'time'} within the next 6 months.`;
    }

    if (!(datetime >= DateTime.now().minus({ hour: 1 }))) {
        return 'A Curation cannot be scheduled in the past.';
    }
};

export const getEndDateTimeError = (endDatetime: DateTime, sendDatetime: DateTime) => {
    if (!endDatetime?.isValid) {
        return 'Please enter a valid date and time.';
    }

    if (!(endDatetime >= sendDatetime)) {
        return 'A Curation cannot end before the send date.';
    }
};

export const minDate = () => DateTime.now().minus({ hour: 1 });
export const maxDate = () => DateTime.now().plus({ month: 6 });

export const minTime = (sendDatetime: string) =>
    DateTime.fromISO(sendDatetime).hasSame(minDate(), 'day') ? minDate() : null;
export const maxTime = (sendDatetime: string) =>
    DateTime.fromISO(sendDatetime).hasSame(maxDate(), 'day') ? DateTime.now() : null;

export const getRecurrenceLabel = (recurrenceFrequency: SendRecurrenceFrequency, sendDatetime?: string) => {
    if (recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS) {
        return `continual`;
    }
    if (recurrenceFrequency === SendRecurrenceFrequency.EVERY_WEEKDAY) {
        return `${recurrenceFrequency} (Monday - Friday)`;
    }
    if (recurrenceFrequency === SendRecurrenceFrequency.WEEKLY && sendDatetime) {
        return `weekly on ${DateTime.fromISO(sendDatetime).toFormat('cccc')}`;
    }
    return recurrenceFrequency ?? '';
};

export const getIsRecurrenceContinuous = (recurrenceFrequency: SendRecurrenceFrequency) =>
    recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS;

export const getIsRecurrenceEnabled = (recurrenceFrequency: SendRecurrenceFrequency) =>
    Boolean(recurrenceFrequency) && recurrenceFrequency !== SendRecurrenceFrequency.DOES_NOT_REPEAT;

type GetCriteriaDetailProps = {
    contentId?: number;
    delayFields?: DelayFields; // pass for sends before save
    delayInterval?: string; // pass for saved sends
    firstEmailRecurrenceFrequency?: SendRecurrenceFrequency;
    scheduledTimestamp?: string;
};
const getCriteriaDetail = ({
    contentId,
    delayFields,
    delayInterval,
    firstEmailRecurrenceFrequency,
    scheduledTimestamp,
}: GetCriteriaDetailProps) => {
    const isEmail = !!contentId;
    if (isEmail) {
        const parentAudienceDelayInterval =
            delayInterval ?? getFormattedParentAudienceDelayInterval(delayFields?.delay, delayFields?.unit);

        return {
            contentNodeId: `${contentId}`,
            contentId,
            firstEmailRecurrenceFrequency,
            parentAudienceDelayInterval,
            scheduledTimestamp,
        };
    }

    return { description: 'exit curation' };
};

type SendWithDelayFields = Partial<Send> & {
    delayFields?: DelayFields;
};
type GetConditionalSplitCurationDetailsProps = {
    firstSend: SendWithDelayFields;
    noCriteriaSend: SendWithDelayFields;
    segments: Segment[];
    yesCriteriaSend: SendWithDelayFields;
};
export const getConditionalSplitCurationDetails = ({
    firstSend,
    noCriteriaSend,
    segments,
    yesCriteriaSend,
}: GetConditionalSplitCurationDetailsProps): Detail[] => {
    const isSegmentsDidOpen =
        // This check is for campaigns before save
        segments[0]?.name === 'opened_sends' ||
        // This check is for saved campaigns
        // (1st segment is user-generated, 2nd/3rd segment is system-generated based on conditional split criteria)
        segments[1]?.segmentDefinition?.engagement[0]?.name === 'opened_sends';
    const splitDescription = `${isSegmentsDidOpen ? CurationSegments.DID_OPEN : CurationSegments.DID_CLICK} email`;

    const initialSendDetail = {
        contentNodeId: `${firstSend.id}`,
        contentId: firstSend.id,
        recurrenceFrequency: firstSend.recurrenceFrequency,
        scheduledEndTimestamp: firstSend.scheduledEndTimestamp,
        scheduledTimestamp: firstSend.scheduledTimestamp,
    };
    const splitDetail = { header: 'Conditional Split', description: splitDescription };
    const meetsCriteriaDetail = getCriteriaDetail({
        contentId: yesCriteriaSend?.id,
        delayFields: yesCriteriaSend?.delayFields,
        delayInterval: yesCriteriaSend?.parentAudienceDelayInterval,
        firstEmailRecurrenceFrequency: firstSend.recurrenceFrequency,
        scheduledTimestamp: yesCriteriaSend?.scheduledTimestamp,
    });
    const doesNotMeetCriteriaDetail = getCriteriaDetail({
        contentId: noCriteriaSend?.id,
        delayFields: noCriteriaSend?.delayFields,
        delayInterval: noCriteriaSend?.parentAudienceDelayInterval,
        firstEmailRecurrenceFrequency: firstSend.recurrenceFrequency,
        scheduledTimestamp: noCriteriaSend?.scheduledTimestamp,
    });

    return [
        initialSendDetail,
        splitDetail,
        {
            header: 'Meets Criteria',
            ...meetsCriteriaDetail,
        },
        {
            header: 'Does Not Meet Criteria',
            ...doesNotMeetCriteriaDetail,
        },
    ];
};

/**
 * Convert segmentDefinition to segments array for UI
 */
export const convertSegmentDefinitionToSegments = (
    segmentDefinition?: SegmentDefinition,
    sends: any[] = []
): SegmentFilterState<SegmentFilterDemographicFields | SegmentFilterEngagementFields | SegmentFilterEventFields>[] => {
    if (!segmentDefinition) {
        return [];
    }

    const segments: SegmentFilterState<any>[] = [];

    if (segmentDefinition.demographic && segmentDefinition.demographic.length > 0) {
        segmentDefinition.demographic.forEach((demographic, index) => {
            segments.push(getDemographicFilter(demographic, index));
        });
    }

    if (segmentDefinition.engagement && segmentDefinition.engagement.length > 0) {
        segmentDefinition.engagement.forEach((engagement, index) => {
            segments.push(getEngagementFilter(engagement, segments.length + index, sends as Send[]));
        });
    }
    if (segmentDefinition.contactEvent && segmentDefinition.contactEvent.length > 0) {
        segmentDefinition.contactEvent.forEach((event, index) => {
            segments.push(getEventFilter(event, segments.length + index));
        });
    }
    if (segmentDefinition.contactObject && segmentDefinition.contactObject.length > 0) {
        segmentDefinition.contactObject.forEach((event, index) => {
            segments.push(getEventFilter(event, segments.length + index, FilterCategory.CONTACT_OBJECTS));
        });
    }

    return segments;
};

/**
 * Transform draft send from API to CampaignSend format
 */
export const transformDraftSendFromApi = (apiSend: any, allSends: any[] = []) => {
    // Convert NOOP to EMAIL for UI, preserve split criteria
    if (apiSend.channel === CampaignChannels.NOOP) {
        const transformedSend: any = {
            ...apiSend,
            channel: CampaignChannels.EMAIL,
        };

        if (apiSend.segmentDefinition) {
            transformedSend.segments = convertSegmentDefinitionToSegments(apiSend.segmentDefinition, allSends);
        } else if (apiSend.segments && apiSend.segments.length > 0) {
            transformedSend.segments = apiSend.segments.map((engagement: SegmentEngagement, index: number) =>
                getEngagementFilter(engagement, index, allSends as Send[])
            );
        }

        return transformedSend;
    }

    if (apiSend.channel === CampaignChannels.EMAIL) {
        const transformedSend: any = {
            ...apiSend,
            subjectLine: apiSend.subjectLine || '',
            preHeader: apiSend.preHeader || '',
        };

        if (apiSend.segmentDefinition) {
            transformedSend.segments = convertSegmentDefinitionToSegments(apiSend.segmentDefinition, allSends);
        } else if (apiSend.segments && apiSend.segments.length > 0) {
            transformedSend.segments = apiSend.segments.map((engagement: SegmentEngagement, index: number) =>
                getEngagementFilter(engagement, index, allSends as Send[])
            );
        }

        return transformedSend;
    }

    if (apiSend.channel === CampaignChannels.DIRECT_MAIL) {
        if (apiSend.contentId) {
            const { contentId, contentName, ...rest } = apiSend;
            return {
                ...rest,
                content: {
                    id: contentId,
                    name: contentName || '',
                },
            };
        }
        // Ensure content property exists even when contentId is not present
        // This matches the structure expected by DirectMailContentStep component
        // and prevents "Cannot read properties of undefined (reading 'id')" errors
        return {
            ...apiSend,
            content: {
                id: null,
                name: '',
            },
        };
    }

    if (apiSend.channel === CampaignChannels.SMS) {
        // Normalize SMS sender profile: API may return smsSenderProfileId or senderProfileId
        const smsSenderProfileIdFromApi = apiSend.smsSenderProfileId ?? apiSend.senderProfileId;

        if (apiSend.smsContentId) {
            const { smsContentId, contentName, ...rest } = apiSend;
            return {
                ...rest,
                ...(smsSenderProfileIdFromApi != null && { smsSenderProfileId: smsSenderProfileIdFromApi }),
                content: {
                    id: smsContentId,
                    name: contentName || '',
                },
            };
        }
        // Ensure content property exists even when smsContentId is not present
        // This matches the structure expected by SmsContentStep component
        // and prevents "Cannot read properties of undefined (reading 'id')" errors
        return {
            ...apiSend,
            ...(smsSenderProfileIdFromApi != null && { smsSenderProfileId: smsSenderProfileIdFromApi }),
            content: {
                id: null,
                name: '',
            },
        };
    }

    if (apiSend.channel === CampaignChannels.VOICE_CALL) {
        return apiSend;
    }

    return apiSend;
};

/**
 * Calculate which fields are different between two email sends for A/B testing
 * @param sendA - First email send
 * @param sendB - Second email send
 * @returns Array of ABTestVariant indicating which fields are different
 */
export const calculateComparisonFields = (sendA: EmailSendInputState, sendB: EmailSendInputState): ABTestVariant[] => {
    const differences: ABTestVariant[] = [];

    // Compare subject line
    if (sendA.subjectLine !== sendB.subjectLine) {
        differences.push(ABTestVariant.SUBJECT_LINE);
    }

    // Compare preview text (preHeader)
    if (sendA.preHeader !== sendB.preHeader) {
        differences.push(ABTestVariant.PREVIEW_TEXT);
    }

    // Compare content (templateId)
    if (sendA.templateId !== sendB.templateId) {
        differences.push(ABTestVariant.BODY);
    }

    // Compare sender profile
    if (sendA.senderProfileId !== sendB.senderProfileId) {
        differences.push(ABTestVariant.SENDER_PROFILE);
    }

    // Compare schedule timestamp
    if (sendA.scheduledTimestamp !== sendB.scheduledTimestamp) {
        differences.push(ABTestVariant.SCHEDULE_TIME);
    }

    return differences;
};
